<template>
  <div class="flex min-h-screen flex-col bg-white">
    <Head :title="$t('booking.history_title')" />

    <DynamicBlock v-if="header" :block="header" />

    <main class="mx-auto w-full max-w-[1200px] flex-1 px-4 py-8 sm:px-6 lg:px-0">
      <!-- Breadcrumbs -->
      <nav class="mb-8 flex min-w-0 flex-wrap items-center gap-1 text-sm" aria-label="Breadcrumb">
        <Link href="/" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
          {{ $t('breadcrumbs.home') }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <span class="min-w-0 break-words text-[#0369a1]">{{ $t('booking.history_title') }}</span>
      </nav>

      <section class="flex flex-col items-center gap-6 py-12 lg:gap-12 lg:py-16">
        <!-- Title -->
        <div class="flex flex-col items-center text-center text-[#262626]">
          <p class="text-sm font-semibold uppercase leading-5 tracking-[1px]">
            {{ $t('booking.history_overline') }}
          </p>
          <h1 class="text-[32px] font-medium leading-[40px] tracking-[-0.02em] lg:text-[36px] lg:leading-[44px]">
            {{ $t('booking.history_title') }}
          </h1>
        </div>

        <!-- Status filter badges. Pokazujemy tylko statusy, ktore realnie
             wystepuja (liczby z bazy przez /api/wizyty), a nie zestaw z Figmy.
             Mobile: pozioma, przewijana lista. sm+: wysrodkowane, zawijane badge. -->
        <div
          v-if="visibleFilters.length"
          class="flex w-full items-center gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:justify-center sm:overflow-visible sm:pb-0"
        >
          <button
            v-for="filter in visibleFilters"
            :key="filter.key"
            type="button"
            class="flex h-8 shrink-0 items-center justify-center gap-1 rounded-lg py-1 pl-2 pr-3 transition-colors"
            :class="activeFilter === filter.key
              ? 'bg-[#111827] text-white'
              : 'bg-[#f3f4f6] text-[#374151] hover:bg-neutral-200'"
            @click="toggleFilter(filter.key)"
          >
            <span class="text-sm font-medium leading-4">{{ $t(filter.label) }}</span>
            <span
              class="flex size-5 items-center justify-center overflow-clip rounded-full p-0.5"
              :class="activeFilter === filter.key ? 'bg-white' : 'bg-[#6b7280]'"
            >
              <span
                class="text-center text-[10px] font-medium leading-[14px]"
                :class="activeFilter === filter.key ? 'text-[#111827]' : 'text-white'"
              >
                {{ filterCount(filter) }}
              </span>
            </span>
          </button>
        </div>

        <!-- Visit cards -->
        <div
          v-if="visibleBookings.length"
          class="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8"
        >
          <article
            v-for="booking in visibleBookings"
            :key="booking.id"
            class="relative flex flex-col overflow-hidden rounded-xl bg-white shadow-[0px_6px_15px_-2px_rgba(16,24,40,0.08)]"
          >
            <div class="flex flex-col gap-3 px-4 py-3 sm:px-6">
              <!-- Status badge -->
              <span
                class="flex items-center justify-center gap-2 self-start rounded-full px-3 py-1.5"
                :class="statusBadge(booking).wrapClass"
              >
                <Icon
                  v-if="statusBadge(booking).icon"
                  :name="statusBadge(booking).icon"
                  class="size-4 shrink-0"
                />
                <span class="text-sm font-medium leading-4">{{ statusBadge(booking).label }}</span>
              </span>

              <div class="flex w-full flex-col gap-2">
                <!-- Komunikat bledu (status: failed). Figma 7999:11559. -->
                <p v-if="isFailed(booking)" class="text-sm font-medium leading-4 text-[#ef4444]">
                  {{ $t('booking.error_generic') }}
                </p>
                <div class="flex w-full flex-col">
                  <!-- Service -->
                  <div class="flex items-center gap-2 py-3.5">
                    <Icon name="briefcase" class="size-4 shrink-0 text-[#1f2937]" />
                    <p class="text-sm leading-4 text-[#111827]">
                      <span v-if="booking.service?.name">{{ booking.service.name }} </span>
                      <span v-if="serviceMeta(booking)" class="font-semibold text-[#16a34a]">
                        ({{ serviceMeta(booking) }})
                      </span>
                    </p>
                  </div>
                  <!-- Date -->
                  <div v-if="bookingDateLabel(booking)" class="flex items-center gap-2 py-3.5">
                    <Icon name="calendar-days" class="size-4 shrink-0 text-[#1f2937]" />
                    <p class="text-sm leading-4 text-[#111827]">{{ bookingDateLabel(booking) }}</p>
                  </div>
                </div>

                <!--
                  Zachowana logika z poprzednich wizyt (SpecialistBookingPanel).
                  Na nowym widoku indeksu jeszcze jej nie pokazujemy -
                  odkomentujemy przy dostarczeniu widokow pojedynczej wizyty
                  i pozostalych statusow.

                  <p v-if="bookingAmountLabel(booking)" class="text-sm font-medium text-neutral-800">
                    {{ bookingAmountLabel(booking) }}
                  </p>
                  <a
                    v-if="booking.output?.patientUrl"
                    :href="booking.output.patientUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="cursor-pointer text-sm font-medium text-[#2ea8cf] hover:underline"
                  >
                    {{ $t('booking.join_visit') }}
                  </a>
                -->

                <!-- Actions: gradientowy przycisk zalezny od statusu - "Oplac wizyte"
                     (oczekujace) / "Umow ponownie" (blad, zakonczona lub anulowana) -
                     + link do szczegolow. Figma 7999:10559 / 7999:12462 / 7999:12473. -->
                <div class="flex items-center gap-3">
                  <a
                    v-if="canPayBooking(booking)"
                    :href="paymentHref(booking)"
                    class="flex h-12 shrink-0 items-center justify-center gap-2 rounded-lg px-5 py-3.5 text-base font-semibold leading-[18px] text-white no-underline drop-shadow-[0px_2px_3px_rgba(16,24,40,0.06)]"
                    style="background-image: linear-gradient(160.691deg, rgb(46, 168, 207) 0%, rgb(40, 182, 155) 100%)"
                  >
                    {{ $t('booking.pay_visit') }}
                  </a>
                  <Link
                    v-else-if="canRebook(booking)"
                    :href="rebookHref(booking)"
                    class="flex h-12 shrink-0 items-center justify-center gap-2 rounded-lg px-5 py-3.5 text-base font-semibold leading-[18px] text-white no-underline drop-shadow-[0px_2px_3px_rgba(16,24,40,0.06)]"
                    style="background-image: linear-gradient(160.691deg, rgb(46, 168, 207) 0%, rgb(40, 182, 155) 100%)"
                  >
                    {{ $t('booking.rebook') }}
                  </Link>
                  <Link
                    :href="bookingDetailsHref(booking)"
                    class="flex h-12 shrink-0 items-center justify-center gap-2 rounded-lg px-5 py-3.5 text-[#149dca] no-underline hover:underline"
                  >
                    <span class="text-base font-medium leading-[18px]">{{ $t('booking.details_short') }}</span>
                    <svg class="size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>

            <!-- Cancel (top-right) -->
            <button
              v-if="canCancelBooking(booking)"
              type="button"
              class="absolute right-3 top-3 flex h-10 items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium leading-4 text-[#ef4444] drop-shadow-[0px_2px_3px_rgba(16,24,40,0.06)] transition-colors hover:bg-red-50 disabled:opacity-50"
              :disabled="cancelingId === booking.id"
              @click="cancelBooking(booking)"
            >
              {{ cancelingId === booking.id ? $t('booking.canceling') : $t('booking.cancel_visit') }}
            </button>
            <a
              v-else-if="canShowCancelUrl(booking)"
              :href="booking.output.cancelUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="absolute right-3 top-3 flex h-10 items-center justify-center rounded-lg px-4 py-2.5 text-sm font-medium leading-4 text-[#ef4444] drop-shadow-[0px_2px_3px_rgba(16,24,40,0.06)] transition-colors hover:bg-red-50 no-underline"
            >
              {{ $t('booking.cancel_visit') }}
            </a>

            <!--
              Ukrywanie wizyty z historii (removeBooking) - do przywrocenia razem
              z pozostala logika starych wizyt.

              <button
                type="button"
                class="absolute right-2 top-2 flex size-6 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
                :aria-label="$t('booking.history_remove')"
                @click="dismissBooking(booking.id)"
              >
                &times;
              </button>
            -->
          </article>
        </div>

        <!-- Empty state -->
        <div v-else class="flex flex-col items-center gap-4 py-10 text-center">
          <p class="text-base leading-6 text-neutral-600">{{ $t('booking.history_empty') }}</p>
          <Link
            :href="specialistsIndexHref"
            class="flex items-center gap-2 rounded-lg bg-[#149dca] px-5 py-3 text-base font-semibold leading-[18px] text-white no-underline transition-colors hover:bg-[#1189b1]"
          >
            {{ $t('booking_details.go_to_specialists') }}
          </Link>
        </div>
      </section>
    </main>

    <DynamicBlock v-if="footer" :block="footer" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import Layout from '@/Layouts/Main.vue';
import DynamicBlock from '@/Shared/DynamicBlock.vue';
import Icon from '@/Shared/Icon.vue';
import { formatServiceMeta } from '@/utils/serviceMeta';
import { formatBookingDate } from '@/utils/bookingDate';
import { deriveVisitStatus, isPastBooking } from '@/utils/visitStatus';
import {
  getBookings,
  mergeBookings,
  // removeBooking, // do przywrocenia razem z ukrywaniem wizyt z historii
  saveBooking,
} from '@/utils/bookingStorage';

// Filtry statusow pokazywane jako plakietki nad lista wizyt. Kolejnosc = kolejnosc
// w projekcie Figma. `statuses` mapuje na wartosci bookingVisitStatus.
// "Umowione" grupuje wizyty umowione (registered) oraz oplacone (paid) - osobnej
// zakladki "Oplacone" nie pokazujemy. Karta i tak rozroznia je plakietka statusu.
const STATUS_FILTERS = [
  { key: 'registered', label: 'booking.filter_booked', statuses: ['registered', 'paid'] },
  { key: 'pending_payment', label: 'booking.filter_pending', statuses: ['pending_payment'] },
  { key: 'failed', label: 'booking.filter_failed', statuses: ['failed'] },
  { key: 'completed', label: 'booking.filter_completed', statuses: ['completed'] },
  { key: 'payment_canceled', label: 'booking.filter_canceled', statuses: ['payment_canceled'] },
];

export default {
  name: 'FrontofficeVisits',
  components: { Head, Link, DynamicBlock, Icon },
  layout: Layout,
  props: {
    header: { type: Object, default: null },
    footer: { type: Object, default: null },
  },
  data() {
    return {
      cachedBookings: [],
      statusFilters: STATUS_FILTERS,
      activeFilter: null,
      cancelingId: null,
      syncingBookings: false,
    };
  },
  computed: {
    specialistsIndexHref() {
      return this.$routes.specialists_index();
    },
    // Tylko filtry z realnymi wizytami (count > 0) - zestaw badge'y wynika
    // z danych w bazie, nie ze sztywnego zestawu z Figmy.
    visibleFilters() {
      return this.statusFilters.filter((filter) => this.filterCount(filter) > 0);
    },
    visibleBookings() {
      if (!this.activeFilter) return this.cachedBookings;
      const filter = this.statusFilters.find((item) => item.key === this.activeFilter);
      if (!filter) return this.cachedBookings;
      return this.cachedBookings.filter((booking) =>
        filter.statuses.includes(this.bookingVisitStatus(booking)));
    },
  },
  mounted() {
    this.loadCachedBookings();
    this.refreshBookingsFromServer();
    window.addEventListener('focus', this.onWindowFocus);
    window.addEventListener('storage', this.loadCachedBookings);
  },
  beforeUnmount() {
    window.removeEventListener('focus', this.onWindowFocus);
    window.removeEventListener('storage', this.loadCachedBookings);
  },
  methods: {
    loadCachedBookings() {
      this.cachedBookings = getBookings();
      // Reset filtra, ktory po synchronizacji nie ma juz zadnych wizyt.
      if (this.activeFilter && !this.visibleFilters.some((filter) => filter.key === this.activeFilter)) {
        this.activeFilter = null;
      }
    },
    onWindowFocus() {
      this.loadCachedBookings();
      this.refreshBookingsFromServer();
    },
    async refreshBookingsFromServer() {
      const ids = this.cachedBookings.map((booking) => booking.id).filter(Boolean);
      if (!ids.length || this.syncingBookings) return;

      this.syncingBookings = true;
      try {
        const params = new URLSearchParams({ ids: ids.join(',') });
        const response = await fetch(`/api/wizyty?${params.toString()}`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;
        const data = await response.json();
        if (Array.isArray(data.visits)) {
          mergeBookings(data.visits);
          this.loadCachedBookings();
        }
      } catch {
        // Offline / blad API - zostawiamy lokalny cache bez zmian.
      } finally {
        this.syncingBookings = false;
      }
    },
    toggleFilter(key) {
      this.activeFilter = this.activeFilter === key ? null : key;
    },
    filterCount(filter) {
      return this.cachedBookings.filter((booking) =>
        filter.statuses.includes(this.bookingVisitStatus(booking))).length;
    },
    serviceMeta(booking) {
      return formatServiceMeta(booking.service);
    },
    bookingDateLabel(booking) {
      return formatBookingDate(booking.output?.visit?.start || booking.chosenDate);
    },
    bookingDetailsHref(booking) {
      return `/wizyty/${encodeURIComponent(booking.id)}`;
    },
    isPast(booking) {
      return isPastBooking(booking);
    },
    bookingVisitStatus(booking) {
      return deriveVisitStatus(booking);
    },
    statusBadge(booking) {
      const status = this.bookingVisitStatus(booking);
      const map = {
        paid: { label: this.$t('booking.status_paid'), wrapClass: 'bg-[#16a34a] text-[#f0fdf4]', icon: 'check' },
        registered: { label: this.$t('booking.status_registered'), wrapClass: 'bg-[#16a34a] text-[#f0fdf4]', icon: 'check' },
        pending_payment: { label: this.$t('booking.status_pending_payment'), wrapClass: 'bg-[#fff4e5] text-[#b36900]', icon: null },
        payment_canceled: { label: this.$t('booking.status_canceled'), wrapClass: 'bg-[#6b7280] text-white', icon: null },
        failed: { label: this.$t('booking.status_failed'), wrapClass: 'bg-[#fef2f2] text-[#ef4444]', icon: null },
        completed: { label: this.$t('booking.status_completed'), wrapClass: 'bg-[#e5e7eb] text-[#111827]', icon: null },
      };
      return map[status] || map.pending_payment;
    },
    isFailed(booking) {
      return this.bookingVisitStatus(booking) === 'failed';
    },
    isCompleted(booking) {
      return this.bookingVisitStatus(booking) === 'completed';
    },
    isCanceled(booking) {
      return this.bookingVisitStatus(booking) === 'payment_canceled';
    },
    // Kiedy oferujemy "Umow ponownie": wizyty zamkniete/nieudane (blad, zakonczona,
    // anulowana) - patrz Figma 7999:12462 / 7999:12473.
    canRebook(booking) {
      return this.isFailed(booking) || this.isCompleted(booking) || this.isCanceled(booking);
    },
    canCancelBooking(booking) {
      // Zamkniete / nieudane wizyty nie maja anulowania - zamiast tego "Umow ponownie".
      if (this.isFailed(booking) || this.isCompleted(booking) || this.isCanceled(booking)) return false;
      if (booking.canCancel === true) return true;
      if (booking.canCancel === false) return false;
      return this.bookingVisitStatus(booking) === 'pending_payment';
    },
    // Link Medfile cancelUrl tylko dla aktywnych wizyt (nie zakonczonych / anulowanych / blednych).
    canShowCancelUrl(booking) {
      if (this.isFailed(booking) || this.isCompleted(booking) || this.isCanceled(booking)) return false;
      return !!booking.output?.cancelUrl;
    },
    // "Oplac wizyte" - dostepne tylko dla wizyt oczekujacych na platnosc,
    // ktore maja adres platnosci (z odpowiedzi API / cache localStorage).
    canPayBooking(booking) {
      if (booking.canPay === true) return true;
      if (booking.canPay === false) return false;
      return this.bookingVisitStatus(booking) === 'pending_payment' && !!this.paymentHref(booking);
    },
    paymentHref(booking) {
      return booking.output?.paymentUrl || booking.payment?.redirect_uri || null;
    },
    // "Umow ponownie" - formularz rezerwacji danej uslugi (#umow-wizyte).
    // Bez slug uslugi: profil specjalisty, a jak i tego brak - lista specjalistow.
    rebookHref(booking) {
      const serviceSlug = booking?.service?.slug;
      if (serviceSlug) return `${this.$routes.service(serviceSlug)}#umow-wizyte`;
      if (!booking?.specialistSlug) return this.specialistsIndexHref;
      return `${this.$routes.specialist(booking.specialistSlug)}#umow-wizyte`;
    },
    async cancelBooking(booking) {
      if (!booking?.id || this.cancelingId) return;
      if (!window.confirm(this.$t('booking.cancel_confirm'))) return;

      this.cancelingId = booking.id;
      try {
        const response = await fetch(`/api/wizyty/${encodeURIComponent(booking.id)}/cancel`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
        });
        const data = await response.json().catch(() => ({}));

        if (data.cancelUrl) {
          window.open(data.cancelUrl, '_blank', 'noopener,noreferrer');
        }

        if (data.visit) {
          saveBooking(data.visit);
          this.loadCachedBookings();
          return;
        }

        if (!response.ok) {
          window.alert(data.error || this.$t('booking.cancel_error'));
        }
      } catch {
        window.alert(this.$t('booking.cancel_error'));
      } finally {
        this.cancelingId = null;
      }
    },

    // ---------------------------------------------------------------------
    // Zachowana logika wyswietlania z poprzednich wizyt (SpecialistBookingPanel).
    // Nieuzywana jeszcze na nowym widoku - odkomentujemy przy dostarczeniu
    // widokow pojedynczej wizyty oraz pozostalych statusow.
    // ---------------------------------------------------------------------
    // dismissBooking(id) {
    //   removeBooking(id);
    //   this.loadCachedBookings();
    // },
    // bookingAmountLabel(booking) {
    //   const cents = booking.payment?.amount_cents;
    //   if (cents == null && booking.service?.price == null) return '';
    //   const amount = cents != null ? Number(cents) / 100 : Number(booking.service.price);
    //   if (Number.isNaN(amount)) return '';
    //   return `${amount.toFixed(2)} ${booking.payment?.currency || 'PLN'}`;
    // },
  },
};
</script>
